import { isConditionallyActive } from './conditionality';
import PHONE_CONSTRAINT_NAMES from '../Data/phoneConstraintNames';
import { ComponentTypes, GenericAdmiralFields } from '../Data/Components';

export const findComponentByFieldName = (fieldName, fieldValue, components) => {
  const foundComponentOrError = {};
  const keyOfSearchedComponentObject = Object.keys(components).find(
    (componentObjectKey) => {
      const { component } = components[componentObjectKey];
      if (
        component &&
        component.mappedQuestionId?.toLowerCase() === fieldName.toLowerCase()
      ) {
        return true;
      }
      if (
        component &&
        component.text.toLowerCase() === fieldName.toLowerCase()
      ) {
        return true;
      }
      return false;
    }
  );

  const foundComponentObject = components[keyOfSearchedComponentObject];
  if (foundComponentObject) {
    foundComponentOrError.componentId = foundComponentObject.component.id;
    foundComponentOrError.fieldType = foundComponentObject.component.type;
    /* For radio button fields, checkboxes and select dropdown fields, an
       exact match of the provided fieldValue with one of the valid options
       needs to be verified, as the value wouldn't have any effect on the UI.
       For all other fields, invalid values will be caught by the server-side
       form validations. It would be inefficient to bulk-validate them all here.
      */
    if (
      ['radios', 'select', 'checkboxes'].includes(
        foundComponentObject.component.type
      )
    ) {
      const selectedOption = foundComponentObject.input.options.find(
        (option) =>
          option.key.toLowerCase() === fieldValue.toLowerCase() ||
          option.value.toLowerCase() === fieldValue.toLowerCase()
      );
      if (selectedOption) {
        foundComponentOrError.optionId = selectedOption.id;
      } else {
        foundComponentOrError.errorObject = {
          message:
            `The provided key-value pair '${fieldName}' => ` +
            `'${fieldValue}' caused an error:\n` +
            'No valid question option could be found for the value' +
            `'${fieldValue}'\n` +
            'The identified question is:\n' +
            `'${foundComponentObject.component.text}'`,
          operation: 'Send data into loaded form widget',
        };
      }
    }
  } else {
    foundComponentOrError.errorObject = {
      message:
        `The provided key-value pair '${fieldName}' => ` +
        `'${fieldValue}' caused an error:\n` +
        'No correspondent question could be found on this form for the key' +
        `'${fieldName}'\n`,
      operation: 'Send data into loaded form widget',
    };
  }
  return foundComponentOrError;
};

export const componentContainsCheckboxOptions = (pageComponent) =>
  pageComponent.component.type === 'checkboxes' ||
  pageComponent.component.type === 'supplier-match' ||
  pageComponent.component.type === 'select-and-connect';

export const getDisplayValueFromComponent = (pageComponent) => {
  if (pageComponent.component.type === 'radios') {
    return getRadioDisplayValueFromComponent(pageComponent);
  }

  if (componentContainsCheckboxOptions(pageComponent)) {
    return getCheckboxDisplayValueFromComponent(pageComponent);
  }

  if (pageComponent.component.type === 'select') {
    return getSelectDisplayValueFromComponent(pageComponent);
  }

  if (pageComponent.component.type === 'datepicker') {
    return getDateDisplayValueFromComponent(pageComponent);
  }

  if (pageComponent.component.type === 'hidden') {
    const value = pageComponent.component.defaultValue
      ? pageComponent.component.defaultValue
      : '';
    return value;
  }

  return getTextfieldDisplayValueFromComponent(pageComponent);
};

const isFirstNameField = (pageComponent) =>
  pageComponent?.component?.mappedQuestionId ===
  GenericAdmiralFields.FIRST_NAME;

const getFirstName = (fullName) => {
  const trimmedFullName = fullName.trim();
  const nameParts = trimmedFullName.split(/\s+/);
  return nameParts[0];
};

const getTextfieldDisplayValueFromComponent = (pageComponent) => {
  const value = pageComponent?.input?.value;
  if (!value) {
    return '';
  }
  return isFirstNameField(pageComponent) ? getFirstName(value) : value;
};

const getDateDisplayValueFromComponent = (pageComponent) => {
  if (!pageComponent?.input?.value) {
    return '';
  }

  const options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  const localDate = new Intl.DateTimeFormat(navigator.language, options).format(
    pageComponent.input.value
  );
  return localDate;
};

const getRadioDisplayValueFromComponent = (pageComponent) => {
  const selectedOption = pageComponent.input.options.find(
    (option) =>
      pageComponent.input.selectedOptions?.length > 0 &&
      String(option.id) === pageComponent.input.selectedOptions?.[0]
  );

  let value = selectedOption ? selectedOption.value : '';

  if (selectedOption && selectedOption.id === 'other-option') {
    value = pageComponent.input.otherValue;
  }

  return value.trim();
};

const getCheckboxDisplayValueFromComponent = (pageComponent) => {
  if (pageComponent.input.selectedOptions === undefined) {
    return [];
  }

  const selectedOptions = pageComponent.input.options.filter(
    (option) =>
      pageComponent.input.selectedOptions.indexOf(String(option.id)) !== -1
  );

  const selectedValues = selectedOptions.map((option) => {
    if (option.id === 'other-option') {
      return pageComponent.input.otherValue ?? '';
    }
    return option.value;
  });

  const trimmedValues = selectedValues.map((value) => value.trim());

  if (trimmedValues.length === 0) {
    return '';
  }

  if (trimmedValues.length === 1) {
    return trimmedValues[0];
  }

  const lastValue = trimmedValues.pop();
  return `${trimmedValues.join(', ')} & ${lastValue}`;
};

export const getSelectDisplayValueFromComponent = (pageComponent) => {
  if (pageComponent.component.type !== 'select') {
    return null;
  }

  let value = '';
  if (
    pageComponent.input.selectedOptions !== undefined &&
    pageComponent.input.selectedOptions.length > 0
  ) {
    const selectedOption = pageComponent.input.options.find(
      (option) => String(option.id) === pageComponent.input.selectedOptions[0]
    );
    if (selectedOption) {
      value = selectedOption.value;
    }
  }

  return value.trim();
};

export const getValueFromComponent = (pageComponent) => {
  if (pageComponent.component.type === 'radios') {
    return getRadioValueFromComponent(pageComponent);
  }

  if (componentContainsCheckboxOptions(pageComponent)) {
    return getCheckboxValuesFromComponent(pageComponent);
  }

  if (pageComponent.component.type === 'select') {
    return getSelectValueFromComponent(pageComponent);
  }

  if (pageComponent.component.type === 'hidden') {
    const value = pageComponent.component.defaultValue
      ? pageComponent.component.defaultValue
      : '';
    return value;
  }

  return pageComponent.input.value;
};

export const getRadioValueFromComponent = (pageComponent) => {
  const selectedOption = pageComponent.input.options.find(
    (option) =>
      pageComponent.input.selectedOptions?.length > 0 &&
      // TODO on conditional components which are not shown there is no selection process so there are no selectedOptions - if we only want to send the completed components are part of the submission filter them out, else this needs to remain
      String(option.id) === pageComponent.input.selectedOptions?.[0]
  );
  let value = selectedOption ? selectedOption.key : '';

  if (selectedOption && selectedOption.id === 'other-option') {
    value = pageComponent.input.otherValue;
  }

  return value;
};

export const getCheckboxValuesFromComponent = (pageComponent) => {
  if (pageComponent.input.selectedOptions === undefined) {
    return [];
  }

  const selectedOptions = pageComponent.input.options.filter(
    (option) =>
      pageComponent.input.selectedOptions.indexOf(String(option.id)) !== -1
  );

  const selectedKeys = selectedOptions.map((option) => {
    if (option.id === 'other-option') {
      return pageComponent.input.otherValue;
    }
    return option.key;
  });

  return selectedKeys;
};

export const getSelectValueFromComponent = (pageComponent) => {
  if (pageComponent.component.type !== 'select') {
    return null;
  }

  let value = '';
  if (
    pageComponent.input.selectedOptions !== undefined &&
    pageComponent.input.selectedOptions.length > 0
  ) {
    const selectedOption = pageComponent.input.options.find(
      (option) => String(option.id) === pageComponent.input.selectedOptions[0]
    );
    if (selectedOption) {
      value = selectedOption.key;
    }
  }

  return value;
};

const tierMappings = (tier) => {
  if (tier > 1) {
    return 'high-value';
  }

  if (tier === 1) {
    return 'low-value';
  }

  return '';
};

export const getOptionTier = (pageComponent) => {
  if (
    ['radios', 'checkboxes', 'select'].includes(pageComponent.component.type) &&
    pageComponent.component?.isTiered === true
  ) {
    const answerOptions = pageComponent.input.options;

    if (
      !pageComponent.input?.selectedOptions ||
      pageComponent.input.selectedOptions.length === 0
    ) {
      return '';
    }

    const { selectedOptions } = pageComponent.input;

    const tierValues = selectedOptions.map((optionId) => {
      const selectedAnswer = answerOptions.find(
        (option) => String(option.id) === String(optionId)
      );

      return selectedAnswer.tier;
    });

    return tierMappings(Math.max(...tierValues));
  }

  return '';
};

export const getFirstEditableComponentOnPage = (
  pageIndex,
  components,
  pages
) => {
  if (!pages) {
    return null;
  }

  const componentIdsOnPage = pages[pageIndex];

  if (!componentIdsOnPage) {
    return null;
  }

  const nonEditableComponentTypes = [
    'html',
    'hint',
    'hidden',
    'text',
    'loader',
    'newsletter-signup',
  ];

  const componentIdsOnPageWithEditableFields = componentIdsOnPage.filter(
    (componentIdOnPage) => {
      const componentOnSamePage = Object.values(components).find(
        (component) => component.component.id === componentIdOnPage
      );
      return !nonEditableComponentTypes.includes(
        componentOnSamePage?.component.type
      );
    }
  );

  const componentOnPageWithEditableField = Object.values(components).find(
    (component) =>
      component.component.id === componentIdsOnPageWithEditableFields[0]
  );

  if (componentOnPageWithEditableField) {
    return componentOnPageWithEditableField;
  }

  return null;
};

export const getPageNumbersWithPhoneQuestion = (components) => {
  const phoneQuestionPageNumbers = [];
  Object.values(components).forEach((component) => {
    if (PHONE_CONSTRAINT_NAMES.includes(component.component.mappedQuestionId)) {
      phoneQuestionPageNumbers.push(component.component.page);
    }
  });
  return phoneQuestionPageNumbers;
};

export const getPageNumbersWithVisibleSupplierMatches = (components) => {
  const supplierMatchPageNumbers = [];
  Object.values(components).forEach((component) => {
    if (
      component.component.type === ComponentTypes.SUPPLIER_MATCH &&
      (component.component.isVisible ||
        isConditionallyActive(components, component))
    ) {
      supplierMatchPageNumbers.push(component.component.page);
    }
  });
  return supplierMatchPageNumbers;
};

export const getPageNumbersWithEmailQuestion = (components) => {
  const emailQuestionPageNumbers = [];
  Object.values(components).forEach((component) => {
    if (component.component.mappedQuestionId === GenericAdmiralFields.EMAIL) {
      emailQuestionPageNumbers.push(component.component.page);
    }
  });
  return emailQuestionPageNumbers;
};

export const getOptionValueFromId = (component, optionId) => {
  if (!component?.input?.options || component.input.options.length === 0)
    return '';

  const selectedOption = component.input.options.find(
    (option) => String(option.id) === String(optionId)
  );
  return selectedOption ? selectedOption.value : '';
};

export const filterComponentsForPage = (components, pageComponentIds) => {
  const componentsOnPage = {};

  Object.entries(components).forEach(([key, value]) => {
    const componentId = Number(key.slice(10)); // i.e. remove component_ and convert type
    if (pageComponentIds && pageComponentIds.includes(componentId)) {
      componentsOnPage[key] = value;
    }
  });

  return componentsOnPage;
};

export const isRadioWithoutOtherOption = (component) => {
  if (component.component.type === 'radios') {
    const hasOtherOption = component.input.options.some(
      (option) => option.id === 'other-option'
    );

    if (!hasOtherOption) {
      return true;
    }
  }

  return false;
};

export const getComponentsBeforePage = (components, targetPage) => {
  const filteredComponents = Object.values(components).filter((component) => {
    const { page } = component.component;
    return page <= targetPage;
  });

  return filteredComponents.reduce((acc, component) => {
    // eslint-disable-next-line no-param-reassign
    acc[`component_${component.component.id}`] = component;
    return acc;
  }, {});
};
